import { UploadIcon } from '@/assets/SVGs/icons/UploadIcon';
import AppButton from '@/components/ui/AppButton';
import AppInput from '@/components/ui/AppInput';
import AppRadioButton from '@/components/ui/AppRadioButton';
import FormErrorMessage from '@/components/ui/FormErrorMessage';
import { endpoints } from '@/services/endpoints';
import { axiosGetRequest, axiosPatchRequest } from '@/services/Request';
import { getBookImageUrl, IBookData } from '@/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { FieldValues, SubmitHandler, useForm } from 'react-hook-form';
import { toast } from 'react-toastify';
import * as z from 'zod';
import { X } from 'lucide-react';

const editBookSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().min(1, 'Description is required'),
  price: z.number(),
  images: z.any().optional(),
  bookType: z.string().min(1, 'Post type is required'),
  category: z.string().min(1, 'Category is required'),
  subCategory: z.string().min(1, 'Sub-category is required'),
  bookEdition: z.number().positive('Edition must be a positive number'),
  bookCondition: z.string().min(1, 'Condition is required'),
  numberOfBooks: z.number().positive('Number of books must be a positive number'),
  educationTerm: z.string().min(1, 'Education term is required'),
  educationType: z.string().min(1, 'Education type is required'),
  location: z.string().min(1, 'Location is required'),
  city: z.string().min(1, 'City is required'),
});

type EditBookFormValues = z.infer<typeof editBookSchema>;

function getEntityId(value: string | { _id?: string } | undefined): string {
  if (!value) return '';
  return typeof value === 'object' ? value._id ?? '' : value;
}

function bookToFormValues(book: IBookData): EditBookFormValues {
  return {
    title: book.title ?? '',
    description: book.description ?? '',
    price: book.price ?? 0,
    bookType: book.bookType ?? '',
    category: getEntityId(book.category as string | { _id?: string }),
    subCategory: getEntityId(book.subCategory as string | { _id?: string }),
    bookEdition: book.bookEdition ?? 1,
    bookCondition: book.bookCondition ?? '',
    numberOfBooks: book.numberOfBooks ?? 1,
    educationTerm: book.educationTerm ?? '',
    educationType: book.educationType ?? '',
    location: book.location ?? '',
    city: book.city ?? '',
  };
}

interface EditBookModalProps {
  bookId: string;
  book?: IBookData;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function EditBookModal({ bookId, book, open, onClose, onSuccess }: EditBookModalProps) {
  const queryClient = useQueryClient();

  const { data: fetchedBook, isLoading: isBookLoading } = useQuery({
    queryKey: [endpoints.getSpecificBook, 'edit', bookId],
    queryFn: () => axiosGetRequest(endpoints.getSpecificBook, { id: bookId }),
    select: (data): IBookData => data.result,
    enabled: open && !!bookId && !book,
  });

  const bookData = book ?? fetchedBook;

  const { data: allCategories } = useQuery({
    queryKey: [endpoints.getAllCategories],
    queryFn: () => axiosGetRequest(endpoints.getAllCategories),
    select: data => data?.result,
    enabled: open,
  });

  const { data: allSubCategories } = useQuery({
    queryKey: [endpoints.getAllSubCategory],
    queryFn: () => axiosGetRequest(endpoints.getAllSubCategory),
    select: data => data?.result,
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<EditBookFormValues>({
    resolver: zodResolver(editBookSchema),
  });

  const selectedImages = watch('images');
  const selectedCategory = watch('category');

  useEffect(() => {
    if (bookData && open) {
      reset(bookToFormValues(bookData));
    }
  }, [bookData, open, reset]);

  const { mutate: updateBook, isPending } = useMutation({
    mutationFn: (formData: FormData) =>
      axiosPatchRequest(endpoints.updateBook, formData, { id: bookId }),
    onError: () => toast.error('Failed to update book'),
    onSuccess: () => {
      toast.success('Book updated successfully');
      queryClient.invalidateQueries({ queryKey: [endpoints.getSpecificBook] });
      queryClient.invalidateQueries({ queryKey: [endpoints.getAllBooks] });
      onSuccess?.();
      onClose();
    },
  });

  const onSubmit: SubmitHandler<FieldValues> = (data: FieldValues) => {
    const formData = new FormData();
    for (const key in data) {
      if (key === 'images') {
        if (data.images?.length) {
          Array.from(data.images as FileList).forEach((file) => formData.append('images', file));
        }
      } else if (data[key] !== undefined && data[key] !== null) {
        formData.append(key, String(data[key]));
      }
    }
    updateBook(formData);
  };

  if (!open) return null;

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-coal-900/40 backdrop-blur-sm" onClick={onClose} />
      <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
        <div className="relative flex max-h-[90vh] w-full max-w-lg flex-col rounded-xl border border-coal-200 bg-white shadow-dropdown">
          <div className="flex items-center justify-between border-b border-coal-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-coal-900">Edit Book</h2>
            <button
              type="button"
              className="rounded-md p-1 text-coal-400 hover:bg-coal-100 hover:text-coal-600"
              onClick={onClose}
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {isBookLoading && !book ? (
            <div className="p-6 text-sm text-coal-500">Loading book details...</div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="flex min-h-0 flex-1 flex-col">
              <div className="space-y-4 overflow-auto px-6 py-4">
                <div>
                  <label htmlFor="edit-title" className="mb-1.5 block text-sm font-medium text-coal-700">Book Title</label>
                  <AppInput id="edit-title" type="text" {...register('title')} />
                  <FormErrorMessage message={errors.title?.message} />
                </div>

                <div>
                  <label htmlFor="edit-description" className="mb-1.5 block text-sm font-medium text-coal-700">Description</label>
                  <AppInput id="edit-description" type="text" {...register('description')} />
                  <FormErrorMessage message={errors.description?.message} />
                </div>

                <div>
                  <label htmlFor="edit-price" className="mb-1.5 block text-sm font-medium text-coal-700">Price</label>
                  <AppInput id="edit-price" type="text" {...register('price', { valueAsNumber: true })} />
                  <FormErrorMessage message={errors.price?.message} />
                </div>

                <div>
                  <p className="mb-1.5 text-sm font-medium text-coal-700">Current Images</p>
                  {bookData?.images?.length ? (
                    <div className="mb-2 flex flex-wrap gap-2">
                      {bookData.images.map((image) => (
                        <img
                          key={image.key || image.url}
                          src={getBookImageUrl(image)}
                          alt=""
                          className="h-14 w-14 rounded-md border border-coal-200 object-cover"
                        />
                      ))}
                    </div>
                  ) : (
                    <p className="mb-2 text-xs text-coal-400">No images</p>
                  )}
                  <label htmlFor="edit-images" className="mb-1.5 block text-sm font-medium text-coal-700">
                    Replace Images (optional)
                  </label>
                  <label htmlFor="edit-images" className="cursor-pointer">
                    <div className="flex flex-col items-center gap-2 rounded-lg border-2 border-dashed border-coal-300 p-5 transition-colors hover:border-primary hover:bg-primary-50">
                      <UploadIcon height={32} width={32} className="text-primary" />
                      <p className="text-center text-sm text-coal-500">
                        {selectedImages?.length
                          ? `${selectedImages.length} new image(s) selected`
                          : 'Upload up to 5 images to replace existing ones'}
                      </p>
                    </div>
                  </label>
                  <input
                    multiple
                    accept="image/*"
                    id="edit-images"
                    type="file"
                    {...register('images')}
                    className="hidden"
                  />
                  <FormErrorMessage message={errors.images?.message} />
                </div>

                <div>
                  <p className="mb-1.5 text-sm font-medium text-coal-700">Post type</p>
                  <div className="flex gap-5">
                    {['offered', 'requested'].map((type) => (
                      <div key={type} className="flex items-center gap-2">
                        <AppRadioButton
                          value={type}
                          type="radio"
                          id={`edit-${type}`}
                          {...register('bookType')}
                          onChange={(e) => setValue('bookType', e.target.value)}
                        />
                        <label htmlFor={`edit-${type}`} className="cursor-pointer text-sm capitalize text-coal-600">
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormErrorMessage message={errors.bookType?.message} />
                </div>

                <div>
                  <label htmlFor="edit-category" className="mb-1.5 block text-sm font-medium text-coal-700">Category</label>
                  <select
                    id="edit-category"
                    {...register('category')}
                    className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 transition-colors hover:border-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select a category</option>
                    {allCategories?.map((category: { _id: string; name_en?: string; name_ar?: string }) => (
                      <option key={category._id} value={category._id}>
                        {category.name_en || category.name_ar || category._id}
                      </option>
                    ))}
                  </select>
                  <FormErrorMessage message={errors.category?.message} />
                </div>

                <div>
                  <label htmlFor="edit-subCategory" className="mb-1.5 block text-sm font-medium text-coal-700">Sub-Category</label>
                  <select
                    id="edit-subCategory"
                    {...register('subCategory')}
                    className="h-9 w-full rounded-lg border border-coal-300 bg-white px-3 text-sm text-coal-900 transition-colors hover:border-coal-400 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  >
                    <option value="">Select a sub-category</option>
                    {allSubCategories
                      ?.filter((sub: { category?: string | { _id?: string } }) => {
                        if (!selectedCategory) return true;
                        const catId = typeof sub.category === 'object' ? sub.category?._id : sub.category;
                        return catId === selectedCategory;
                      })
                      .map((sub: { _id: string; name_en?: string; name_ar?: string; category?: { name_en?: string } }) => (
                        <option key={sub._id} value={sub._id}>
                          {sub.name_en || sub.name_ar || sub._id}
                          {sub.category?.name_en ? ` (${sub.category.name_en})` : ''}
                        </option>
                      ))}
                  </select>
                  <FormErrorMessage message={errors.subCategory?.message} />
                </div>

                <div>
                  <label htmlFor="edit-bookEdition" className="mb-1.5 block text-sm font-medium text-coal-700">Book Edition</label>
                  <AppInput id="edit-bookEdition" type="text" {...register('bookEdition', { valueAsNumber: true })} />
                  <FormErrorMessage message={errors.bookEdition?.message} />
                </div>

                <div>
                  <p className="mb-1.5 text-sm font-medium text-coal-700">Book Condition</p>
                  <div className="flex gap-5">
                    {['new', 'used'].map((condition) => (
                      <div key={condition} className="flex items-center gap-2">
                        <AppRadioButton
                          value={condition}
                          type="radio"
                          id={`edit-condition-${condition}`}
                          {...register('bookCondition')}
                          onChange={(e) => setValue('bookCondition', e.target.value)}
                        />
                        <label htmlFor={`edit-condition-${condition}`} className="cursor-pointer text-sm capitalize text-coal-600">
                          {condition}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormErrorMessage message={errors.bookCondition?.message} />
                </div>

                <div>
                  <label htmlFor="edit-numberOfBooks" className="mb-1.5 block text-sm font-medium text-coal-700">Number of Books</label>
                  <AppInput id="edit-numberOfBooks" type="text" {...register('numberOfBooks', { valueAsNumber: true })} />
                  <FormErrorMessage message={errors.numberOfBooks?.message} />
                </div>

                <div>
                  <p className="mb-1.5 text-sm font-medium text-coal-700">Select Term</p>
                  <div className="flex gap-5">
                    {['first', 'second'].map((term) => (
                      <div key={term} className="flex items-center gap-2">
                        <AppRadioButton
                          value={term}
                          type="radio"
                          id={`edit-term-${term}`}
                          {...register('educationTerm')}
                          onChange={(e) => setValue('educationTerm', e.target.value)}
                        />
                        <label htmlFor={`edit-term-${term}`} className="cursor-pointer text-sm capitalize text-coal-600">
                          {term}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormErrorMessage message={errors.educationTerm?.message} />
                </div>

                <div>
                  <p className="mb-1.5 text-sm font-medium text-coal-700">Select Education type</p>
                  <div className="flex gap-5">
                    {['azhar', 'general'].map((type) => (
                      <div key={type} className="flex items-center gap-2">
                        <AppRadioButton
                          value={type}
                          type="radio"
                          id={`edit-edu-${type}`}
                          {...register('educationType')}
                          onChange={(e) => setValue('educationType', e.target.value)}
                        />
                        <label htmlFor={`edit-edu-${type}`} className="cursor-pointer text-sm capitalize text-coal-600">
                          {type}
                        </label>
                      </div>
                    ))}
                  </div>
                  <FormErrorMessage message={errors.educationType?.message} />
                </div>

                <div>
                  <label htmlFor="edit-location" className="mb-1.5 block text-sm font-medium text-coal-700">Location</label>
                  <AppInput id="edit-location" type="text" {...register('location')} />
                  <FormErrorMessage message={errors.location?.message} />
                </div>

                <div>
                  <label htmlFor="edit-city" className="mb-1.5 block text-sm font-medium text-coal-700">City</label>
                  <AppInput id="edit-city" type="text" {...register('city')} />
                  <FormErrorMessage message={errors.city?.message} />
                </div>
              </div>

              <div className="flex gap-3 border-t border-coal-200 px-6 py-4">
                <AppButton type="button" theme="default" className="flex-1" onClick={onClose}>
                  Cancel
                </AppButton>
                <AppButton loading={isPending} size="md" type="submit" className="flex-1">
                  Save Changes
                </AppButton>
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}
